import { AfterViewInit, Component, inject } from '@angular/core';
import { LocaleStringMap, fadeUp, vshrink } from 'shared';
import { DownloadFileEvent, Question, QuestionFileModel, Serializer, SurveyModel, UploadFilesEvent } from 'survey-core';

import { FromLocaleStringMapPipe } from '../../../pipes/from-locale-string-map.pipe';
import { FormSubmissionService } from '../../../services';
import { SurveyComponent } from '../../entity-components/survey/survey.component';
import { IconComponent } from '../../util/icon/icon.component';
import { BasePopupComponent, HorizontalAnchor, VerticalAnchor } from '../base-popup/base-popup.component';

type FileMap = Map<string, { url: string; contentType: string }>;

@Component({
    selector: 'app-survey-entry-popup',
    templateUrl: './survey-entry-popup.component.html',
    styleUrls: ['./survey-entry-popup.component.scss'],
    animations: [fadeUp('popup'), vshrink('listItem'), vshrink('listHeader')],
    imports: [IconComponent, SurveyComponent, FromLocaleStringMapPipe],
})
export class SurveyEntryPopupComponent extends BasePopupComponent<SurveyEntryPopupComponent> implements AfterViewInit {
    private readonly formSubmissionService = inject(FormSubmissionService);

    public results: object;
    public model: object;
    public formId: string;
    protected title?: LocaleStringMap;

    protected surveyModel: SurveyModel;

    public constructor() {
        super();
        this.verticalAnchor = VerticalAnchor.Center;
        this.horizontalAnchor = HorizontalAnchor.Center;
        this.popupOptions.closeByEsc = false;
        this.popupOptions.closeByClickOutside = false;
        this.popupOptions.padding = 0;

        Serializer.getProperty('file', 'storeDataAsText').defaultValue = false;
        Serializer.getProperty('file', 'waitForUpload').defaultValue = true;
    }

    public ngAfterViewInit(): void {
        const tempFileMap = new Map<string, { url: string; contentType: string }>();

        // Copy input model
        const model: {
            title?: LocaleStringMap;
            description?: LocaleStringMap;
        } = structuredClone(this.model);

        // Extract title for popup title
        this.title = structuredClone(model.title);

        // Remove title and description from survey model to prevent them being rendered in-form
        if (model.title) delete model.title;
        if (model.description) delete model.description;

        // Parse survey model
        const surveyModel = new SurveyModel(model);

        // Make sure file upload questions store data as text
        surveyModel.getAllQuestions(false, undefined, true).forEach((question: Question) => {
            if (question.getType() === 'file') {
                const fileQuestion = question as QuestionFileModel;
                fileQuestion.storeDataAsText = false;
                fileQuestion.waitForUpload = true;
            }
        });

        surveyModel.onUploadFiles.add((_, options) => this.onUploadFiles(tempFileMap, options));
        surveyModel.onDownloadFile.add((_sender, options) => this.onDownloadFile(tempFileMap, options));

        // Update the file upload questions in the model
        this.surveyModel = surveyModel;
        surveyModel.onComplete.add(this.onSurveyComplete.bind(this));
    }

    private onUploadFiles(tempFileMap: FileMap, options: UploadFilesEvent): void {
        this.formSubmissionService
            .uploadFile(options.files, this.formId, options.name)
            .then((result) => {
                const items = result.map((response, _index) => {
                    tempFileMap.set(response.id, { url: response.url, contentType: response.contentType });

                    return {
                        file: { name: response.id, type: response.contentType },
                        content: response.id,
                    };
                });

                options.callback('success', items);
            })
            .catch((error) => {
                options.callback('error', error);
            });
    }

    private onDownloadFile(tempFileMap: FileMap, options: DownloadFileEvent): void {
        const fileId = options.content;

        if (!tempFileMap.has(fileId)) {
            options.callback('error', 'File not found');
            return;
        }

        const selectedFile = tempFileMap.get(fileId);

        fetch(selectedFile.url)
            .then((response) => response.blob())
            .then((blob) => {
                const file = new File([blob], options.fileValue.name, { type: selectedFile.contentType });
                const reader = new FileReader();
                reader.onload = (e): void => options.callback('success', e.target.result);
                reader.readAsDataURL(file);
            })
            .catch((_error) => {
                options.callback('error');
            });
    }

    private onSurveyComplete(model: SurveyModel): void {
        this.results = model.data;
        this.close(this.results);
    }
}
